import React, { useEffect, useRef, useState } from "react";
import { Autocomplete, Box, Checkbox, Grid, InputAdornment, MenuItem, Stack, TextField, Typography, useTheme, Button, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { toast } from "react-toastify";
import { listAllBookings, listAllHotels, resetAllBookings, resetAllHotelsList } from "../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { tableStyle } from "../mui-styles/table";
import { StatusChip } from "../../components/Chips";
import { PaginationFooter } from "../../components";
import { CalendarMonth, Download } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import moment from "moment";

const Bookings = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const dispatch = useDispatch();
    const startInputRef = useRef();
    const endInputRef = useRef();
    const [hotelId, setHotelId] = useState("");
    const [startDate, setStartDate] = useState(0);
    const [endDate, setEndDate] = useState(0);
    const [dateType, setDateType] = useState("checkin");
    const [localPage, setLocalPage] = useState(1);
    const [status, setStatus] = useState([]);
    const [bookingFrom, setBookingFrom] = useState([]);
    const [otas, setOtas] = useState([]);
    const [type, setType] = useState([]);
    const allBookingFroms = [
        { name: "Direct booking", value: "direct_booking" },
        { name: "OTA", value: "ota" },
        { name: "Booking engine", value: "booking_engine" },
    ];
    const allStatuses = [
        { name: "Confirmed", value: "confirmed" },
        { name: "Cancelled", value: "cancelled" },
        { name: "Pending", value: "pending" },
    ];
    const availableOtas = ["makemytrip", "agoda", "goibibo", "booking.com"];
    const allTypes = [
        { name: "B2B", value: "b2b" },
        { name: "B2C", value: "b2c" },
    ];

    useEffect(() => {
        dispatch(listAllHotels());
        dispatch(resetAllBookings());
        // Call api on initial page load with empty hotelId
        dispatch(listAllBookings({ hotelId: "" }));
        return () => {
            dispatch(resetAllHotelsList());
        }
    }, [dispatch]);

    // useEffect(() => {
    //     if (startDate && endDate) {
    //         dispatch(resetAllBookings());
    //         fetchMoreData({ startDate, endDate });
    //     }
    //     // eslint-disable-next-line
    // }, [startDate, endDate])

    const fetchMoreData = (page) => {
        if (page?.pageSize !== undefined) {
            setLocalPage(1);
        }

        let payload = {
            hotelId: hotelId || "",
            ...page,
            status: status.join() || undefined,
            type: type.join() || undefined,
            bookingFrom: [...bookingFrom, ...otas].join() || undefined
        };

        if (dateType === 'checkin') {
            payload.startDate = startDate;
            payload.endDate = endDate;
        } else {
            payload.bookedAtStartDate = startDate;
            payload.bookedAtEndDate = endDate;
        }

        dispatch(listAllBookings(payload));
    }

    const handleSubmit = () => {
        dispatch(resetAllBookings());
        fetchMoreData();
    };

    const { bookingsList, hotels, pageNumber, pageSize, count, totals } = useSelector(
        ({
            admin: {
                bookings: { list, pageNumber, pageSize, count, totals },
                hotels,
            },
        }) => ({
            bookingsList: list,
            pageNumber, pageSize, count, totals,
            hotels,
        })
    );

    const optionsList = hotels?.list?.map((el, idx) => ({
        label: el.name,
        id: el._id,
    }))

    const localRatio = pageSize > 100 ? Math.ceil(pageSize / 100) : 1;

    const CustomFooter = () => (
        <PaginationFooter
            count={count}
            pageSize={pageSize}
            pageNumber={pageSize > 100 ? localPage : pageNumber}
            fetchMoreData={(page) => {
                if (
                    pageSize > 100 &&
                    page.pageNumber !== undefined &&
                    page.pageSize === undefined
                ) {
                    const targetServerPage = Math.ceil(page.pageNumber / localRatio);
                    if (targetServerPage !== pageNumber) {
                        fetchMoreData({ pageNumber: targetServerPage });
                    }
                    setLocalPage(page.pageNumber);
                } else {
                    fetchMoreData(page);
                }
            }}
        />
    );

    const localIndexStart = ((localPage - 1) % localRatio) * 100;
    const localIndexEnd = localIndexStart + 100;

    const displayedBookings = pageSize > 100
        ? bookingsList.slice(localIndexStart, localIndexEnd)
        : bookingsList;

    const columns = [
        {
            field: "booked_at",
            headerName: "Booked on",
            width: 120,
            renderCell: ({ row }) => `${row?.booked_at || "-"}`,
        },
        {
            field: "hotel_name",
            headerName: "Hotel",
            width: 200,
            renderCell: ({ row }) => `${row?.hotel_id?.name || "-"}`,
        },
        {
            field: "guest_name",
            headerName: "Guest info",
            width: 220,
            renderCell: ({ row: { guest_name, guest_email } }) => {
                return (
                    <Stack>
                        <Typography color={colors.greenAccent[400]}>
                            <b>{guest_name}</b>
                        </Typography>
                        <Typography fontSize={"small"}>
                            {guest_email || ""}
                        </Typography>
                    </Stack>
                )
            },
        },
        {
            field: "roomtypes",
            headerName: "Roomtype(s)",
            width: 220,
            renderCell: ({ row }) => (<small>{row?.roomtypes?.join(', ') || "-"}</small>)
        },
        {
            field: "checkin_date",
            headerName: "Check-in date",
            width: 120,
            valueGetter: (params) => `${params.row.checkin_date ? moment.unix(params.row.checkin_date).format("YYYY-MM-DD") : "-"}`,
        },
        {
            field: "checkout_date",
            headerName: "Check-out date",
            width: 120,
            valueGetter: (params) => `${params.row.checkout_date ? moment.unix(params.row.checkout_date).format("YYYY-MM-DD") : "-"}`,
        },
        {
            field: "total_price",
            headerName: "Rate",
            width: 100,
            renderCell: ({ row: { custom_price, total_price } }) => `${custom_price ? custom_price : total_price}`,
        },
        {
            field: "paid_amount",
            headerName: "Paid amount",
            width: 100,
            renderCell: ({ row }) => `${row?.paid_amount || 0}`,
        },
        {
            field: "total_rooms",
            headerName: "Total rooms",
            width: 100,
            renderCell: ({ row }) => `${row?.total_rooms || 0}`,
        },
        {
            field: "total_nights",
            headerName: "Total room nights",
            width: 140,
            renderCell: ({ row }) => `${row?.total_nights || 0}`,
        },
        {
            field: "booking_from",
            headerName: "Booking from",
            width: 140,
            renderCell: ({ row }) => `${row?.booking_from || 0}`,
        },
    ];

    return (
        <Box m="20px" mb={15}>
            <Box
                display={"flex"}
                flexDirection={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
                m="20px 0"
            >
                <Header title="BOOKINGS" subtitle="welcome to Bookings" />
            </Box>
            <Grid container spacing={2} mt={2} justifyContent="space-between">
                <Grid item>
                    <Stack direction={"row"} spacing={1} alignItems="center">
                        <ToggleButtonGroup
                            value={dateType}
                            exclusive
                            onChange={(event, newFilterBy) => {
                                if (newFilterBy !== null) {
                                    setDateType(newFilterBy);
                                }
                            }}
                            aria-label="Filter by"
                            size="small"
                        >
                            <ToggleButton value="checkin" aria-label="checkin date">
                                Check-in date
                            </ToggleButton>
                            <ToggleButton value="booked" aria-label="booked date">
                                Booked date
                            </ToggleButton>
                        </ToggleButtonGroup>
                        <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label="Start Date"
                            placeholder="Start Date"
                            sx={{ width: 170 }}
                            InputLabelProps={{ shrink: true }}
                            value={startDate ? moment.unix(startDate).format("YYYY-MM-DD") : ""}
                            onChange={(newValue) => {
                                setStartDate(moment(newValue.target.value, "YYYY-MM-DD").startOf("day").unix());
                                if (endDate !== "" && moment(newValue.target.value).add(1, "days").format("YYYY-MM-DD") > moment.unix(endDate).format("YYYY-MM-DD")) {
                                    setEndDate(moment(newValue.target.value, "YYYY-MM-DD").add(1, "days").endOf("day").unix());
                                }
                                if (!newValue.target.value) {
                                    setStartDate(0)
                                    setEndDate(0)
                                }
                            }}
                            inputRef={startInputRef}
                            onClick={() => startInputRef.current.showPicker()}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <CalendarMonth sx={{ cursor: "pointer" }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label="End Date"
                            placeholder="End Date"
                            sx={{ width: 170 }}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: moment.unix(startDate).format("YYYY-MM-DD") }}
                            value={endDate ? moment.unix(endDate).format("YYYY-MM-DD") : ""}
                            onChange={(newValue) => {
                                setEndDate(moment(newValue.target.value, "YYYY-MM-DD").endOf("day").unix())
                                if (!newValue.target.value) {
                                    setStartDate(0)
                                    setEndDate(0)
                                }
                            }}
                            inputRef={endInputRef}
                            onClick={() => endInputRef.current.showPicker()}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <CalendarMonth sx={{ cursor: "pointer" }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Stack>
                </Grid>
                <Grid item>
                    <Grid container spacing={2}>
                        <Grid item>
                            <TextField
                                select
                                size="small"
                                variant="outlined"
                                label="Status"
                                value={status}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (value.includes("all")) {
                                        if (status.length === allStatuses.length) {
                                            setStatus([]);
                                        } else {
                                            setStatus(allStatuses.map(s => s.value));
                                        }
                                    } else {
                                        setStatus(value);
                                    }
                                }}
                                sx={{ width: 120 }}
                                SelectProps={{ multiple: true }}
                            >
                                <MenuItem value={"all"}>
                                    <Checkbox
                                        size="small"
                                        sx={{ p: "0 5px 0 0" }}
                                        checked={status.length === allStatuses.length && allStatuses.length > 0}
                                        indeterminate={status.length > 0 && status.length < allStatuses.length}
                                    />
                                    <em>All</em>
                                </MenuItem>
                                {allStatuses?.map((e) => (
                                    <MenuItem value={e.value} key={e.value}>
                                        <Checkbox size="small" sx={{ p: "0 5px 0 0" }} checked={status.indexOf(e.value) > -1} />
                                        {e.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item>
                            <TextField
                                select
                                size="small"
                                variant="outlined"
                                label="Booking from"
                                value={bookingFrom}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (value.includes("all")) {
                                        if (bookingFrom.length === allBookingFroms.length) {
                                            setBookingFrom([]);
                                        } else {
                                            setBookingFrom(allBookingFroms.map(s => s.value));
                                        }
                                    } else {
                                        setBookingFrom(value);
                                    }
                                }}
                                sx={{ width: 140 }}
                                SelectProps={{ multiple: true }}
                            >
                                <MenuItem value={"all"}>
                                    <Checkbox
                                        size="small"
                                        sx={{ p: "0 5px 0 0" }}
                                        checked={bookingFrom.length === allBookingFroms.length && allBookingFroms.length > 0}
                                        indeterminate={bookingFrom.length > 0 && bookingFrom.length < allBookingFroms.length}
                                    />
                                    <em>All</em>
                                </MenuItem>
                                {allBookingFroms?.map((e) => (
                                    <MenuItem value={e.value} key={e.value}>
                                        <Checkbox size="small" sx={{ p: "0 5px 0 0" }} checked={bookingFrom.indexOf(e.value) > -1} />
                                        {e.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item>
                            <TextField
                                select
                                size="small"
                                variant="outlined"
                                label="OTAs"
                                value={otas}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (value.includes("all")) {
                                        if (otas.length === availableOtas.length) {
                                            setOtas([]);
                                        } else {
                                            setOtas(availableOtas);
                                        }
                                    } else {
                                        setOtas(value);
                                    }
                                }}
                                sx={{ width: 120 }}
                                SelectProps={{ multiple: true }}
                            >
                                <MenuItem value={"all"} key="all-ota">
                                    <Checkbox
                                        size="small"
                                        sx={{ p: "0 5px 0 0" }}
                                        checked={otas.length === availableOtas.length && availableOtas.length > 0}
                                        indeterminate={otas.length > 0 && otas.length < availableOtas.length}
                                    />
                                    <em>All</em>
                                </MenuItem>
                                {availableOtas.map((e) => (
                                    <MenuItem value={e} key={e}>
                                        <Checkbox size="small" sx={{ p: "0 5px 0 0" }} checked={otas.indexOf(e) > -1} />
                                        {e}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item>
                            <TextField
                                select
                                size="small"
                                variant="outlined"
                                label="Type"
                                value={type}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (value.includes("all")) {
                                        if (type.length === allTypes.length) {
                                            setType([]);
                                        } else {
                                            setType(allTypes.map(s => s.value));
                                        }
                                    } else {
                                        setType(value);
                                    }
                                }}
                                sx={{ width: 100 }}
                                SelectProps={{ multiple: true }}
                            >
                                <MenuItem value={"all"}>
                                    <Checkbox
                                        size="small"
                                        sx={{ p: "0 5px 0 0" }}
                                        checked={type.length === allTypes.length && allTypes.length > 0}
                                        indeterminate={type.length > 0 && type.length < allTypes.length}
                                    />
                                    <em>All</em>
                                </MenuItem>
                                {allTypes?.map((e) => (
                                    <MenuItem value={e.value} key={e.value}>
                                        <Checkbox size="small" sx={{ p: "0 5px 0 0" }} checked={type.indexOf(e.value) > -1} />
                                        {e.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Stack direction={"row"} spacing={1} alignItems="center">
                        <Autocomplete
                            id="combo-box-demo"
                            size='small'
                            options={optionsList}
                            onChange={(e, value) => setHotelId(value?.id ?? "")}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderInput={(params) => <TextField {...params} label="Filter by hotel" />}
                            sx={{ width: 250 }}
                        />
                        <Button variant="contained" color="secondary" onClick={handleSubmit} sx={{ height: '40px' }}>
                            Submit
                        </Button>
                    </Stack>
                </Grid>
            </Grid>

            <Box
                m="8px 0 0 0"
                height="80vh"
                sx={tableStyle(colors)}
            >
                <DataGrid
                    rows={displayedBookings}
                    columns={columns}
                    getRowId={(row) => row?._id}
                    components={{ Footer: CustomFooter }}
                />
            </Box>
            <Box mt={2} p={0.5} border={"1px solid grey"}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-evenly">
                    <Typography>Total Room Nights &nbsp;&nbsp; <b>{totals?.total_nights || 0}</b></Typography>
                    <Typography>Total Cancelled Room Nights &nbsp;&nbsp; <b>{totals?.total_cancelled_nights || 0}</b></Typography>
                    <Typography>Total Confirmed Room Nights &nbsp;&nbsp; <b>{totals?.total_confirmed_nights || 0}</b></Typography>
                    <Typography>Total Amount &nbsp;&nbsp; <b>{totals?.total_amount || 0}</b></Typography>
                </Stack>
            </Box>
        </Box>
    );
};

export default Bookings;
